// frontend/src/hooks/api/useProviderQueries.ts
// React Query hooks for provider-specific operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  providerService, 
  BusinessProfile, 
  ProviderProfile, 
  ServiceArea, 
  ProviderServiceOffering,
  BookingRequest,
  ProviderStats 
} from '@/lib/api/providerService';
import { tokenManager } from '@/lib/httpClient';

// Query keys for provider data
export const providerKeys = {
  all: ['provider'] as const,
  profile: () => [...providerKeys.all, 'profile'] as const,
  areas: () => [...providerKeys.all, 'areas'] as const,
  services: () => [...providerKeys.all, 'services'] as const,
  bookings: () => [...providerKeys.all, 'bookings'] as const,
  booking: (id: number) => [...providerKeys.bookings(), id] as const,
  stats: () => [...providerKeys.all, 'stats'] as const,
  earnings: (period: string) => [...providerKeys.all, 'earnings', period] as const,
  reviews: () => [...providerKeys.all, 'reviews'] as const,
  notifications: () => [...providerKeys.all, 'notifications'] as const,
  verification: () => [...providerKeys.all, 'verification'] as const,
  workingHours: () => [...providerKeys.all, 'working-hours'] as const,
} as const;

// Provider profile queries
export const useProviderProfile = () => {
  return useQuery({
    queryKey: providerKeys.profile(),
    queryFn: async () => {
      const response = await providerService.getProfile();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useUpdateProviderProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData: BusinessProfile) => {
      const response = await providerService.updateProfile(profileData);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: (data) => {
      // Update profile in cache
      queryClient.setQueryData(providerKeys.profile(), (old: any) => ({
        ...old,
        provider: data.provider,
      }));
      // Invalidate profile query to refetch
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

export const useUploadProfileImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await providerService.uploadProfileImage(file);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      // Invalidate profile to refetch with new image
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

// Slug management
export const useCheckSlugAvailability = () => {
  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await providerService.checkSlugAvailability(slug);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
  });
};

export const useUpdateSlug = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await providerService.updateSlug(slug);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

export const useGenerateSlugSuggestions = () => {
  return useMutation({
    mutationFn: async (businessName: string) => {
      const response = await providerService.generateSlugSuggestions(businessName);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
  });
};

// Service areas queries
export const useServiceAreas = () => {
  return useQuery({
    queryKey: providerKeys.areas(),
    queryFn: async () => {
      const response = await providerService.getServiceAreas();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useAddServiceArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (areaData: Omit<ServiceArea, 'area_id' | 'provider_id' | 'created_at'>) => {
      const response = await providerService.addServiceArea(areaData);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.areas() });
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

export const useUpdateServiceArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ areaId, data }: { areaId: number; data: Partial<ServiceArea> }) => {
      const response = await providerService.updateServiceArea(areaId, data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.areas() });
    },
  });
};

export const useDeleteServiceArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (areaId: number) => {
      const response = await providerService.deleteServiceArea(areaId);
      if (response.success) {
        return response;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.areas() });
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

// Provider services queries
export const useProviderServices = () => {
  return useQuery({
    queryKey: providerKeys.services(),
    queryFn: async () => {
      const response = await providerService.getProviderServices();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useAddProviderService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceData: Omit<ProviderServiceOffering, 'service_id' | 'provider_id' | 'created_at' | 'updated_at' | 'service_type'>) => {
      const response = await providerService.addService(serviceData);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.services() });
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

export const useUpdateProviderService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceId, data }: { serviceId: number; data: Partial<ProviderServiceOffering> }) => {
      const response = await providerService.updateService(serviceId, data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.services() });
    },
  });
};

export const useDeleteProviderService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceId: number) => {
      const response = await providerService.deleteService(serviceId);
      if (response.success) {
        return response;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.services() });
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

// Booking requests queries
export const useBookingRequests = (
  page: number = 1,
  limit: number = 10,
  status?: string,
  dateRange?: { start: string; end: string }
) => {
  return useQuery({
    queryKey: [...providerKeys.bookings(), { page, limit, status, dateRange }],
    queryFn: async () => {
      const response = await providerService.getBookingRequests(page, limit, status, dateRange);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useBookingRequest = (requestId: number) => {
  return useQuery({
    queryKey: providerKeys.booking(requestId),
    queryFn: async () => {
      const response = await providerService.getBookingRequest(requestId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken() && !!requestId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useRespondToBookingRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, response: bookingResponse, quote }: { requestId: number; response: 'accept' | 'decline'; quote?: number }) => {
      const response = await providerService.respondToBookingRequest(requestId, bookingResponse, quote);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.bookings() });
      queryClient.invalidateQueries({ queryKey: providerKeys.booking(variables.requestId) });
      queryClient.invalidateQueries({ queryKey: providerKeys.stats() });
    },
  });
};

// Provider stats and analytics
export const useProviderStats = () => {
  return useQuery({
    queryKey: providerKeys.stats(),
    queryFn: async () => {
      const response = await providerService.getStats();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useProviderEarnings = (period: string = 'monthly') => {
  return useQuery({
    queryKey: providerKeys.earnings(period),
    queryFn: async () => {
      const response = await providerService.getEarnings(period);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Reviews and ratings
export const useProviderReviews = () => {
  return useQuery({
    queryKey: providerKeys.reviews(),
    queryFn: async () => {
      const response = await providerService.getReviews();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Verification status
export const useProviderVerification = () => {
  return useQuery({
    queryKey: providerKeys.verification(),
    queryFn: async () => {
      const response = await providerService.getVerificationStatus();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useSubmitVerificationDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documents: FormData) => {
      const response = await providerService.submitVerificationDocuments(documents);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.verification() });
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};

// Working hours
export const useProviderWorkingHours = () => {
  return useQuery({
    queryKey: providerKeys.workingHours(),
    queryFn: async () => {
      const response = await providerService.getWorkingHours();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useUpdateWorkingHours = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workingHours: any) => {
      const response = await providerService.updateWorkingHours(workingHours);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.workingHours() });
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
  });
};